import React, { useState, useMemo } from "react";
import { MainNav } from "@/components/MainNav";
import { Calendar, CalendarEvent } from "@/Calendar";
import { EventsPanel } from "@/components/EventsPanel";
import { DayEvents, Event } from "@/types/event";
import { useCalendarEvents } from "@/hooks/use-calendar-events";
import { useCalendarDate } from "@/hooks/use-calendar-date";
import { useCountrySelection } from "@/hooks/use-country-selection";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { MobileNav } from "@/components/MobileNav";

// Helper function to transform CalendarEvent[] to DayEvents[]
const transformCalendarEvents = (calendarEvents: CalendarEvent[]): DayEvents[] => {
  return calendarEvents.map(calendarEvent => ({
    date: calendarEvent.date,
    events: calendarEvent.events.map((eventDetails, index) => {
      return {
        id: `${calendarEvent.date}-${index}`, // Generate a unique ID
        date: calendarEvent.date,
        country: eventDetails.country || 'Unknown',
        currency: 'USD', // Default currency
        previous: eventDetails.previous || 0,
        estimate: eventDetails.forecast || 0,
        actual: eventDetails.actual || 0,
        change: (eventDetails.actual || 0) - (eventDetails.previous || 0),
        impact: (() => {
          const apiImpact = (eventDetails.impact || 'low').toLowerCase();
          if (apiImpact === 'high') return 'High';
          if (apiImpact === 'medium') return 'Medium';
          return 'Low';
        })() as 'Low' | 'Medium' | 'High',
        rawImpact: eventDetails.impact || 'low',
        unit: eventDetails.unit || '',
        score: eventDetails.score || 0, // Use the original API score
        title: eventDetails.title || eventDetails.event || 'Untitled Event',
        volatility_score: 0,
        volatility_threshold: 0
      };
    }) as Event[]
  }));
};

const Index = () => {
  const [selectedDate, setSelectedDate] = useState<number>();
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const { currentDate } = useCalendarDate();
  const { selectedCountries } = useCountrySelection();
  const { isLoading } = useCalendarEvents(
    currentDate,
    selectedCountries.length > 0 ? selectedCountries : undefined
  );
  const isMobile = useIsMobile();

  // Transform calendar events to day events format
  const events = useMemo(() => {
    return transformCalendarEvents(calendarEvents);
  }, [calendarEvents]);

  const handleDateClick = (date: number) => {
    console.log("Date clicked:", date);
    setSelectedDate(date);
  };

  const handleEventsChange = (newEvents: CalendarEvent[]) => {
    setCalendarEvents(newEvents);
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex-none px-1 sm:px-2 md:px-4 lg:px-6 pt-2 sm:pt-4 md:pt-6 pb-1 sm:pb-2 md:pb-4">
        <MainNav />
      </div>
      <div className="flex-1 px-1 sm:px-2 md:px-4 lg:pr-0 lg:pl-6 pb-2 sm:pb-4 md:pb-6 min-h-0 overflow-hidden">
        <div className={cn(
          "flex w-full h-full",
          isMobile ? "flex-col gap-1" : "flex-row gap-4"
        )}>
          <div className={cn(
            "min-w-0 min-h-0",
            isMobile ? "flex-none" : "flex-1"
          )}>
            <Calendar 
              onDateClick={handleDateClick}
              onEventsChange={handleEventsChange}
            />
          </div>
          <div className={cn(
            "shrink-0 min-h-0",
            isMobile ? "flex-1 min-h-[300px]" : "w-[400px]"
          )}>
            <EventsPanel 
              selectedDate={selectedDate}
              events={calendarEvents}
              isLoading={isLoading}
            />
          </div>
        </div>
      </div>
      {isMobile && <MobileNav />}
    </div>
  );
};

export default Index;
